import { useMutation, useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { useTranslation } from 'react-i18next';

import type { TRegisterDeviceRequest } from '@/components/remote-software/types/register-device';

import useNativeAction from '@/components/remote-software/hooks/useNativeAction';
import { EXTERNAL_NATIVE_APPLICATIONS, setLanguage } from '@/config/EnviromentConfig';
import msalInstance from '@/config/MsalAuthConfig';
import { vpcService } from '@/service/vpc/vpcService';

interface HandshakePayload {
  appName: string;
  deviceApplicationId: string;
  platform: string;
  pushChannel: string;
}

interface HandshakeResponse {
  applicationId: string;
  payload?: HandshakePayload;
  language?: string;
}

interface HandshakePollingOptions {
  maxAttempts?: number;
  intervalMs?: number;
  onTrigger?: (msg: string) => void;
  onResponse?: (msg: string) => void;
  onCancelAction?: () => void;
  onHandshakeComplete?: (isValid: boolean) => void;
  onDetectedNativeEnvChange?: (isDetected: boolean) => void;
}

interface HandshakePollingState {
  attemptCount: number;
  isPolling: boolean;
  completedHandshake: boolean;
  handshakeError: boolean;
}

const useHandshakePolling = ({
  maxAttempts = 10,
  intervalMs = 1500,
  onTrigger,
  onResponse,
  onCancelAction,
  onHandshakeComplete,
  onDetectedNativeEnvChange,
}: HandshakePollingOptions = {}): HandshakePollingState => {
  const account = msalInstance.getActiveAccount();
  const { sendRequestToNative } = useNativeAction();
  const { i18n } = useTranslation();

  const [attemptCount, setAttemptCount] = useState(0);
  const [completedHandshake, setCompletedHandshake] = useState(false);
  const [handshakeError, setHandshakeError] = useState(false);
  const pendingDeviceRegistrationRef = useRef<TRegisterDeviceRequest | null>(null);
  const pendingDeviceRegistrationInFlightRef = useRef(false);

  const isAuthenticated = !!account;

  const { mutate: registerDevice } = useMutation({
    mutationFn: (request: TRegisterDeviceRequest) => vpcService.WebToNative.registerDevice({ request }),
    onSuccess: () => {},
  });

  const flushPendingDeviceRegistration = useCallback(() => {
    if (!isAuthenticated || !pendingDeviceRegistrationRef.current || pendingDeviceRegistrationInFlightRef.current) {
      return;
    }

    const request = pendingDeviceRegistrationRef.current;
    pendingDeviceRegistrationInFlightRef.current = true;

    registerDevice(request, {
      onSettled: () => {
        pendingDeviceRegistrationInFlightRef.current = false;
        if (pendingDeviceRegistrationRef.current === request) {
          pendingDeviceRegistrationRef.current = null;
        }
      },
    });
  }, [isAuthenticated, registerDevice]);

  useEffect(() => {
    flushPendingDeviceRegistration();
  }, [flushPendingDeviceRegistration]);

  const handShakeExternalApp = useCallback(async () => {
    try {
      const response = (await sendRequestToNative('handshake', undefined, {
        onTrigger: (msg: string) => {
          onTrigger?.(msg);
        },
        onResponse: (msg: string) => {
          onResponse?.(msg);
        },
        onCancelAction: () => {
          setHandshakeError(true);
          onDetectedNativeEnvChange?.(false);
          onCancelAction?.();
        },
      })) as HandshakeResponse;

      // Handle the successful response
      if (response && response.applicationId) {
        const isValidApp = EXTERNAL_NATIVE_APPLICATIONS.includes(response.applicationId);

        setCompletedHandshake(isValidApp);
        setHandshakeError(false);
        onHandshakeComplete?.(isValidApp);

        // Handle device registration from handshake response (moved from legacy handler)
        if (isValidApp && response.payload) {
          const registerDeviceRequest: TRegisterDeviceRequest = {
            appName: response.payload.appName,
            deviceApplicationId: response.payload.deviceApplicationId,
            platform: response.payload.platform,
            pushChannel: response.payload.pushChannel,
          };

          if (!isAuthenticated) {
            pendingDeviceRegistrationRef.current = registerDeviceRequest;
            flushPendingDeviceRegistration();
          } else {
            registerDevice(registerDeviceRequest);
          }

          if (response.language) {
            setLanguage(response.language, i18n);
          }
        }
      } else {
        setHandshakeError(true);
        throw new Error('Invalid handshake response - no applicationId');
      }
    } catch (error) {
      setHandshakeError(true);
      throw error;
    }
  }, [
    sendRequestToNative,
    onTrigger,
    onResponse,
    onDetectedNativeEnvChange,
    onCancelAction,
    onHandshakeComplete,
    isAuthenticated,
    registerDevice,
    flushPendingDeviceRegistration,
    i18n,
  ]);

  // Use React Query for handshake polling
  const { isLoading: isPolling } = useQuery({
    queryKey: ['handshake'],
    queryFn: async () => {
      setAttemptCount((prev) => prev + 1);
      try {
        await handShakeExternalApp();
        return null; // handShakeExternalApp doesn't return anything, so return null
      } catch (_error) {
        console.error(_error);
        return null; // Return null on error instead of undefined
      }
    },
    enabled: isMobile && !completedHandshake && attemptCount < maxAttempts,
    retry: false,
    refetchInterval: !completedHandshake && attemptCount < maxAttempts ? intervalMs : false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  return {
    attemptCount,
    isPolling,
    completedHandshake,
    handshakeError,
  };
};

export default useHandshakePolling;
